/**
 * Select Component
 *
 * Custom dropdown select with full keyboard navigation, type-ahead search,
 * and accessibility support. Supports visual variants for different contexts.
 */

import { Component, createSignal, Show, For, onCleanup, onMount } from "solid-js";
import { ChevronIcon } from "~/components/ui/icons";
import styles from "./Select.module.css";
import { useClickOutside } from "~/hooks/ui";

export interface SelectOption {
  value: string;
  label: string;
}

export type SelectVariant = "default" | "onAccent";

export interface SelectProps {
  id: string;
  name: string;
  options: SelectOption[];
  value: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  variant?: SelectVariant;
  class?: string;
  onInput: (value: string) => void;
}

const Select: Component<SelectProps> = (props) => {
  const [isOpen, setIsOpen] = createSignal(false);
  const [highlightedIndex, setHighlightedIndex] = createSignal(-1);
  const [searchQuery, setSearchQuery] = createSignal("");
  const [searchTimeoutId, setSearchTimeoutId] = createSignal<number | null>(null);

  let containerRef: HTMLDivElement | undefined;
  let dropdownRef: HTMLDivElement | undefined;

  const variant = () => props.variant || "default";

  const selectedLabel = () => {
    if (searchQuery()) return searchQuery();
    const option = props.options.find((opt) => opt.value === props.value);
    return option?.label || props.placeholder || "Bitte wählen...";
  };

  const isPlaceholder = () => !props.value && !searchQuery();

  const selectOption = (option: SelectOption) => {
    props.onInput(option.value);
    setIsOpen(false);
    setHighlightedIndex(-1);
    setSearchQuery("");
  };

  const toggleDropdown = () => {
    if (props.disabled) return;
    setIsOpen(!isOpen());
    if (!isOpen()) {
      setHighlightedIndex(-1);
    }
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setHighlightedIndex(-1);
    setSearchQuery("");
  };

  useClickOutside({
    ref: () => containerRef,
    callback: closeDropdown,
    eventName: "mousedown",
  });

  const scrollToHighlighted = () => {
    if (dropdownRef && highlightedIndex() >= 0) {
      const optionElement = dropdownRef.children[highlightedIndex()] as HTMLElement;
      if (optionElement) {
        optionElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  };

  const handleTypeAhead = (key: string) => {
    const currentTimeout = searchTimeoutId();
    if (currentTimeout) clearTimeout(currentTimeout);

    const newQuery = searchQuery() + key.toLowerCase();
    setSearchQuery(newQuery);

    const matchIndex = props.options.findIndex((option) =>
      option.label.toLowerCase().includes(newQuery)
    );

    if (matchIndex !== -1) {
      setHighlightedIndex(matchIndex);
      if (!isOpen()) setIsOpen(true);
      requestAnimationFrame(() => scrollToHighlighted());
    }

    const timeout = setTimeout(() => setSearchQuery(""), 500) as unknown as number;
    setSearchTimeoutId(timeout);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (props.disabled) return;

    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        if (isOpen()) {
          if (highlightedIndex() >= 0) {
            selectOption(props.options[highlightedIndex()]);
          }
        } else {
          setIsOpen(true);
        }
        break;

      case "ArrowDown":
        e.preventDefault();
        if (!isOpen()) {
          setIsOpen(true);
          setHighlightedIndex(0);
        } else {
          const nextIndex =
            highlightedIndex() < props.options.length - 1
              ? highlightedIndex() + 1
              : highlightedIndex();
          setHighlightedIndex(nextIndex);
          requestAnimationFrame(() => scrollToHighlighted());
        }
        break;

      case "ArrowUp":
        e.preventDefault();
        if (isOpen()) {
          const prevIndex = highlightedIndex() > 0 ? highlightedIndex() - 1 : highlightedIndex();
          setHighlightedIndex(prevIndex);
          requestAnimationFrame(() => scrollToHighlighted());
        }
        break;

      case "Escape":
        e.preventDefault();
        closeDropdown();
        break;

      case "Tab":
        closeDropdown();
        break;

      default:
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          e.preventDefault();
          handleTypeAhead(e.key);
        }
        break;
    }
  };

  onMount(() => {
    onCleanup(() => {
      const timeout = searchTimeoutId();
      if (timeout) clearTimeout(timeout);
    });
  });

  return (
    <div
      ref={containerRef}
      class={`${styles.select} ${styles[variant()]} ${props.class || ""}`}
      classList={{
        [styles.open]: isOpen(),
        [styles.disabled]: props.disabled,
        [styles.error]: props.error,
      }}
    >
      <button
        type="button"
        id={props.id}
        class={styles.trigger}
        classList={{ [styles.triggerPlaceholder]: isPlaceholder() }}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen()}
        aria-labelledby={props.id}
        disabled={props.disabled}
      >
        <span class={styles.value}>{selectedLabel()}</span>
        <span class={styles.arrow} aria-hidden="true">
          <ChevronIcon direction="down" />
        </span>
      </button>

      <Show when={isOpen()}>
        <div ref={dropdownRef} class={styles.dropdown} role="listbox">
          <For each={props.options}>
            {(option, index) => (
              <div
                class={styles.option}
                classList={{
                  [styles.optionSelected]: option.value === props.value,
                  [styles.optionHighlighted]: index() === highlightedIndex(),
                }}
                role="option"
                aria-selected={option.value === props.value}
                onClick={() => selectOption(option)}
                onMouseEnter={() => setHighlightedIndex(index())}
              >
                {option.label}
              </div>
            )}
          </For>
        </div>
      </Show>

      {/* Hidden native select for form submission */}
      <select
        name={props.name}
        value={props.value}
        style={{ display: "none" }}
        tabindex={-1}
        aria-hidden="true"
        aria-labelledby={props.id}
      >
        <option value="">{props.placeholder || "Bitte wählen..."}</option>
        <For each={props.options}>
          {(option) => <option value={option.value}>{option.label}</option>}
        </For>
      </select>
    </div>
  );
};

export default Select;
